import { useMemo, useState } from 'react';
import type {
  BuyListItemSelectionInput,
  BuyListTallySelectionInput,
  Quantity,
} from '~/graphql/graphql';
import { toggleId } from '~/utils/toggleId';
import type { BuyListDetailItem } from '~/pages/buy-lists/sections/types';
import type { BuyListDetailTally } from '~/pages/buy-lists/sections/tallies/types';

function withEdit<Value>(
  previous: Map<string, Value>,
  rowId: string,
  value: Value | null
): Map<string, Value> {
  const next = new Map(previous);
  if (value === null) {
    next.delete(rowId);
  } else {
    next.set(rowId, value);
  }
  return next;
}

export function useBuyListItemSelection(
  items: BuyListDetailItem[],
  tallies: BuyListDetailTally[]
) {
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(
    () => new Set(items.map((item) => item.id))
  );
  const [editedQuantitiesByItemId, setEditedQuantitiesByItemId] = useState<
    Map<string, Quantity>
  >(() => new Map());
  const [selectedTallyIds, setSelectedTallyIds] = useState<Set<string>>(
    () => new Set(tallies.map((tally) => tally.id))
  );
  const [editedTallyCountsByTallyId, setEditedTallyCountsByTallyId] = useState<
    Map<string, number>
  >(() => new Map());

  const selectedItems = useMemo(
    () => items.filter((item) => selectedItemIds.has(item.id)),
    [items, selectedItemIds]
  );
  const selectedTallies = useMemo(
    () => tallies.filter((tally) => selectedTallyIds.has(tally.id)),
    [tallies, selectedTallyIds]
  );

  const quantityFor = (item: BuyListDetailItem) =>
    editedQuantitiesByItemId.get(item.id) ?? item.quantity;

  const tallyCountFor = (tally: BuyListDetailTally): number | null =>
    tally.__typename === 'CannedBuyListTally'
      ? (editedTallyCountsByTallyId.get(tally.id) ?? tally.tallyCount)
      : null;

  const hasLineWithoutQuantity = selectedItems.some(
    (item) => quantityFor(item).value <= 0
  );

  const hasNothingSelected =
    selectedItems.length === 0 && selectedTallies.length === 0;

  const toggleItem = (itemId: string) => {
    setSelectedItemIds((previous) => toggleId(previous, itemId));
  };

  const changeQuantity = (itemId: string, quantity: Quantity | null) => {
    setEditedQuantitiesByItemId((previous) =>
      withEdit(previous, itemId, quantity)
    );
  };

  const toggleTally = (tallyId: string) => {
    setSelectedTallyIds((previous) => toggleId(previous, tallyId));
  };

  const changeTallyCount = (tallyId: string, tallyCount: number) => {
    const tally = tallies.find((candidate) => candidate.id === tallyId);
    const isUnchanged =
      tally !== undefined &&
      tally.__typename === 'CannedBuyListTally' &&
      tally.tallyCount === tallyCount;
    setEditedTallyCountsByTallyId((previous) =>
      withEdit(previous, tallyId, isUnchanged ? null : tallyCount)
    );
  };

  const toSelectionInputs = (): {
    items: BuyListItemSelectionInput[];
    tallies: BuyListTallySelectionInput[];
  } => ({
    items: selectedItems.map((item) => ({
      buyListItemId: item.id,
      quantity: quantityFor(item),
    })),
    tallies: selectedTallies.map((tally) => {
      const editedTallyCount = editedTallyCountsByTallyId.get(tally.id);
      return editedTallyCount === undefined
        ? { buyListTallyId: tally.id }
        : { buyListTallyId: tally.id, tallyCount: editedTallyCount };
    }),
  });

  return {
    itemsStepProps: {
      selectedItemIds,
      onToggleItem: toggleItem,
      quantitiesByItemId: editedQuantitiesByItemId,
      onQuantityChange: changeQuantity,
    },
    talliesSectionProps: {
      tallies,
      selectedTallyIds,
      onToggleTally: toggleTally,
      tallyCountFor,
      onTallyCountChange: changeTallyCount,
    },
    selectedItems,
    selectedTallies,
    quantityFor,
    tallyCountFor,
    hasLineWithoutQuantity,
    hasNothingSelected,
    toSelectionInputs,
  };
}
